import React, { ReactNode, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface TabButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  anchorRef?: (el: HTMLButtonElement | null) => void;
}

export const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  (
    {
      children,
      onClick,
      active = false,
      disabled = false,
      className = '',
      anchorRef,
      ...props
    },
    ref
  ) => {
    // Base classes matching the refined aesthetics
    const baseClasses =
      'flex h-[32px] w-auto min-w-[32px] items-center justify-center rounded-none px-2 py-1 text-sm transition-all hover:bg-transparent border-b-2 cursor-pointer relative';

    const activeClasses = active
      ? 'border-b-accent text-accent font-semibold'
      : 'border-b-transparent text-fg-secondary hover:text-fg-primary hover:border-b-border-strong';

    const disabledClasses = disabled ? 'cursor-not-allowed opacity-50' : '';

    const setRefs = React.useCallback(
      (node: HTMLButtonElement | null) => {
        if (anchorRef) anchorRef(node);
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
          }
        }
      },
      [ref, anchorRef]
    );

    return (
      <button
        type="button"
        ref={setRefs}
        onClick={onClick}
        disabled={disabled}
        className={twMerge(baseClasses, activeClasses, disabledClasses, className)}
        {...props}
      >
        {children}
        {active && (
          <div className="absolute bottom-[-2px] left-0 right-0 h-[2px] bg-accent shadow-[0_0_12px_rgba(59,130,246,0.8)]" />
        )}
      </button>
    );
  }
);

TabButton.displayName = 'TabButton';
